import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';

interface HeroSectionProps {
  movies: Movie[];
}

export default function HeroSection({ movies }: HeroSectionProps) {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback(
    (index: number) => {
      if (isTransitioning || index === current) return;
      setIsTransitioning(true);
      setCurrent(index);
      setTimeout(() => setIsTransitioning(false), 600);
    },
    [current, isTransitioning]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % movies.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [current, movies.length, goTo]);

  const movie = movies[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
      <div
        className="absolute inset-0 transition-opacity duration-700"
        style={{ opacity: isTransitioning ? 0.7 : 1 }}
      >
        <img
          src={movie.backdrop}
          alt={movie.title}
          className="w-full h-full object-cover object-top"
        />
      </div>

      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-background-950"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-background-950/70 via-transparent to-background-950/30"></div>

      <div className="absolute bottom-0 left-0 right-0 w-full px-4 md:px-6 pb-10 md:pb-16">
        <div className="max-w-[1400px] mx-auto">
          <div className="max-w-xl">
            <div className="flex items-center gap-3 mb-3">
              {movie.isNew && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-accent-500 text-background-950 font-label font-semibold">
                  最新上映
                </span>
              )}
              {movie.isHot && (
                <span className="text-xs px-2.5 py-1 rounded-full bg-primary-500/20 text-primary-400 font-label font-semibold border border-primary-500/30">
                  热播推荐
                </span>
              )}
              <span className="text-sm text-foreground-300">{movie.year}</span>
              <span className="text-sm text-foreground-400">{movie.duration}</span>
            </div>

            <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-2">
              {movie.title}
            </h2>
            <p className="text-sm md:text-base text-foreground-300 mb-4">{movie.enTitle}</p>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              {movie.genre.map((g) => (
                <span
                  key={g}
                  className="text-xs px-2.5 py-1 rounded-full border border-background-400/30 text-foreground-300"
                >
                  {g}
                </span>
              ))}
            </div>

            <p className="text-sm text-foreground-400 line-clamp-2 mb-6 max-w-md">
              {movie.description}
            </p>

            <div className="flex items-center gap-3">
              <Link
                to={`/detail/${movie.id}`}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-950 text-sm font-label font-semibold hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-fill text-lg"></i>
                立即观看
              </Link>
              <Link
                to={`/detail/${movie.id}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-foreground-300/30 text-foreground-100 text-sm font-label hover:bg-foreground-50/5 transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-information-line text-base"></i>
                详情
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {movies.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
              i === current
                ? 'bg-primary-500 w-6'
                : 'bg-foreground-400/40 hover:bg-foreground-400/60'
            }`}
            aria-label={`切换到第${i + 1}张`}
          />
        ))}
      </div>

      <button
        onClick={() => goTo((current - 1 + movies.length) % movies.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-foreground-100 transition-all cursor-pointer"
        aria-label="上一个"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={() => goTo((current + 1) % movies.length)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-black/40 hover:bg-black/60 text-foreground-100 transition-all cursor-pointer"
        aria-label="下一个"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}